<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import ColorPicker from "@/ui/components/colorPicker/ColorPicker.vue";
import TagChip from "@/ui/components/FileBrowser/TagChip.vue";
import { hexToRgbString, rgbStringToHex } from "@/Utils/color";

const { t } = useI18n();

const NAME_MAX_LENGTH = 60;

const AUTO_COLOR_HEX = "#9E9E9E";

const props = withDefaults(
	defineProps<{
		name?: string;
		rgbColorValue?: string | null;
		takenNames?: string[];
		submitLabel?: string;
		isDeletable?: boolean;
	}>(),
	{
		name: "",
		rgbColorValue: null,
		takenNames: () => [],
		submitLabel: "",
		isDeletable: false,
	}
);

const emit = defineEmits<{
	submit: [name: string, rgbColorValue: string | null];
	delete: [];
}>();

const isOpen = ref(false);
const isPickerVisible = ref(false);
const draftName = ref(props.name);
const draftColor = ref<string | null>(props.rgbColorValue);

const reseed = (): void => {
	draftName.value = props.name;
	draftColor.value = props.rgbColorValue;
	isPickerVisible.value = false;
};

watch(isOpen, reseed);

const trimmedName = computed(() => draftName.value.trim());

const isNameTaken = computed(() => {
	const candidate = trimmedName.value.toLowerCase();

	if (candidate === props.name.trim().toLowerCase()) {
		return false;
	}

	return props.takenNames.some((taken) => taken.toLowerCase() === candidate);
});

const canSubmit = computed(
	() => trimmedName.value.length > 0 && !isNameTaken.value
);

const pickerHex = computed(
	() => (draftColor.value && rgbStringToHex(draftColor.value)) ?? AUTO_COLOR_HEX
);

const onColorUpdated = (picked: string): void => {
	draftColor.value = hexToRgbString(picked) ?? draftColor.value;
	isPickerVisible.value = false;
};

const submit = (): void => {
	if (!canSubmit.value) {
		return;
	}

	emit("submit", trimmedName.value, draftColor.value);
	isOpen.value = false;
};

const requestDelete = (): void => {
	isOpen.value = false;
	emit("delete");
};
</script>

<template>
	<v-menu v-model="isOpen" :close-on-content-click="false" location="bottom">
		<template #activator="{ props: activatorProps }">
			<slot name="activator" :props="activatorProps" />
		</template>

		<v-card min-width="320" class="pa-2 d-flex flex-column ga-2">
			<v-text-field
				v-model="draftName"
				:maxlength="NAME_MAX_LENGTH"
				:error="isNameTaken"
				:messages="isNameTaken ? t('fileBrowser.tags.nameTaken') : ''"
				:label="t('fileBrowser.tags.tagName')"
				autofocus
				@keyup.enter="submit"
				hide-details
			>
				<template v-slot:append-inner>
					<color-picker
						:model-value="pickerHex"
						@color-updated="onColorUpdated"
					/>
				</template>
			</v-text-field>

			<div class="d-flex align-center ga-2">
				<tag-chip
					:name="trimmedName || t('fileBrowser.tags.tagName')"
					:rgb-color-value="draftColor"
				/>
				<span v-if="!draftColor" class="text-caption text-medium-emphasis">
					{{ t("fileBrowser.tags.autoColour") }}
				</span>
			</div>

			<v-divider />

			<v-card-actions class="pa-0">
				<v-btn
					v-if="isDeletable"
					:text="t('fileBrowser.actions.delete')"
					variant="text"
					color="error"
					prepend-icon="fa: fa-regular fa-trash"
					@click="requestDelete"
				/>
				<v-spacer />
				<v-btn
					:text="t('fileBrowser.actions.cancel')"
					variant="text"
					@click="isOpen = false"
				/>
				<v-btn
					:text="submitLabel || t('fileBrowser.actions.save')"
					:disabled="!canSubmit"
					@click="submit"
				/>
			</v-card-actions>
		</v-card>
	</v-menu>
</template>

<style lang="scss" scoped>
.v-card-actions {
	min-height: 0;
}
</style>
